import {
  MERK_ACTIVITY_TYPE,
  MERK_ORDER_SIDE,
  MerkActivity,
} from "@letsbit/malcolmlb";
import { ReactComponent as BuyIcon } from "assets/images/icons/buy-invest.svg";
import { ReactComponent as SellIcon } from "assets/images/icons/sell.svg";
import { ReactComponent as SendIcon } from "assets/images/icons/send-dollars.svg";
import { ReactComponent as TicketIcon } from "assets/images/icons/ticket.svg";
import { ReactComponent as ReceiveIcon } from "assets/images/icons/receive-dollars.svg";
import { ReactComponent as PagesIcon } from "assets/images/icons/multiple-pages.svg";
import { useIntl } from "react-intl";
import { messages } from "./messages";
import CustomFormattedMessage from "components/CustomFormattedMessage";

export const useAlpacaActivitiesUtils = () => {
  const intl = useIntl();

  const getMerkOperationInfo = (
    operation: MerkActivity,
  ): {
    title: string | JSX.Element;
    icon: React.FunctionComponent<React.SVGProps<SVGSVGElement>>;
    asset: string;
    amount: string;
    date: string;
    currency: string;
  } => {
    switch (operation.type) {
      case MERK_ACTIVITY_TYPE.DEPOSIT:
        return {
          icon: ReceiveIcon,
          title: intl.formatMessage(messages.deposit),
          amount: operation.data.amount,
          asset: operation.data.currency,
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.WITHDRAWAL:
        return {
          icon: SendIcon,
          title: intl.formatMessage(messages.withdrawal),
          amount: operation.data.amount,
          asset: operation.data.currency,
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.ORDER:
        if (operation.data.side === MERK_ORDER_SIDE.BUY) {
          return {
            icon: BuyIcon,
            title: (
              <CustomFormattedMessage
                {...messages.buy}
                values={{ symbol: operation?.data?.symbol?.toUpperCase() }}
              />
            ),
            amount: operation.data.amount,
            asset: operation.data.symbol,
            date: operation.data.date,
            currency: operation.data.currency,
          };
        }
        return {
          icon: SellIcon,
          title: (
            <CustomFormattedMessage
              {...messages.sell}
              values={{ symbol: operation?.data?.symbol?.toUpperCase() }}
            />
          ),
          amount: operation.data.received_amount,
          asset: operation.data.symbol,
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.ACAT:
        return {
          icon: SendIcon,
          title: <CustomFormattedMessage {...messages.acatTransfer} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.ACATS:
        return {
          icon: SendIcon,
          title: <CustomFormattedMessage {...messages.transfer} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.CIL:
      case MERK_ACTIVITY_TYPE.CSD:
        return {
          icon: BuyIcon,
          title: <CustomFormattedMessage {...messages.payOnMoney} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.CSW:
        return {
          icon: SendIcon,
          title: <CustomFormattedMessage {...messages.cashWitdrawable} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.DIV:
      case MERK_ACTIVITY_TYPE.DIVCGL:
      case MERK_ACTIVITY_TYPE.DIVCGS:
      case MERK_ACTIVITY_TYPE.DIVROC:
        return {
          icon: BuyIcon,
          title: <CustomFormattedMessage {...messages.dividend} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.DIVNRA:
        return {
          icon: BuyIcon,
          title: <CustomFormattedMessage {...messages.dividendNRA} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.DIVTXEX:
        return {
          icon: BuyIcon,
          title: <CustomFormattedMessage {...messages.dividendExempt} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.FEE:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.fee} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.INT:
        return {
          icon: BuyIcon,
          title: <CustomFormattedMessage {...messages.interests} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.JNLS:
        return {
          icon: ReceiveIcon,
          title: <CustomFormattedMessage {...messages.journalEntry} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.OPASN:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.optionAssignment} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.OPCA:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.optionCorporateAction} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.OPCSH:
        return {
          icon: PagesIcon,
          title: (
            <CustomFormattedMessage {...messages.optionNonStandarContracts} />
          ),
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.OPEXC:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.optionExercise} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.OPEXP:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.optionExpiration} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.OPTRD:
        return {
          icon: SendIcon,
          title: <CustomFormattedMessage {...messages.optionTrade} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.MA:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.mergerAcquisition} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.PTC:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.passThruChange} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.REORG:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.reorgCA} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.SPIN:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.stockSpinOff} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      case MERK_ACTIVITY_TYPE.SPLIT:
        return {
          icon: PagesIcon,
          title: <CustomFormattedMessage {...messages.stockSplit} />,
          amount: operation.data.net_amount,
          asset: "USD",
          date: operation.data.date,
          currency: operation.data.currency,
        };
      default:
        return {
          title: intl.formatMessage(messages.adjustment),
          icon: TicketIcon,
          asset: "",
          amount: "",
          date: "",
          currency: "",
        };
    }
  };

  return {
    getMerkOperationInfo,
  };
};
